import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { useState } from "react";

import AdminLayout from "@/layouts/AdminLayout";
import { extractApiErrorMessage } from "@/api/error-message";
import { useProductsList } from "@/api/features/products/queries";
import { useCreateAffiliatePartnerMutation } from "@/api/features/affiliate-partners/mutations";

import AffiliatePartnerForm, {
  createEmptyAffiliatePartnerForm,
  type AffiliatePartnerFormValues,
} from "./AffiliatePartnerForm";

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((part, idx) => ({
    href: `/${parts.slice(0, idx + 1).join("/")}`,
    label: part
      .split("-")
      .map((segment) => segment[0]?.toUpperCase() + segment.slice(1))
      .join(" "),
  }));

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((crumb, index) => (
          <li key={crumb.href} className="flex items-center gap-1">
            <span>/</span>
            {index < crumbs.length - 1 ? (
              <Link to={crumb.href} className="hover:text-gray-700">
                {crumb.label}
              </Link>
            ) : (
              <span className="text-gray-700">{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function mapFormToBody(value: AffiliatePartnerFormValues) {
  return {
    companyName: value.companyName.trim(),
    website: value.website.trim() || null,
    gstNumber: value.gstNumber.trim().toUpperCase() || null,
    contactFirstName: value.contactFirstName.trim(),
    contactLastName: value.contactLastName.trim(),
    contactEmail: value.contactEmail.trim(),
    contactPhone: value.contactPhone.trim() || null,
    city: value.city.trim() || null,
    state: value.state.trim() || null,
    country: value.country.trim() || null,
    notes: value.notes.trim() || null,
    commissionPercent: value.commissionPercent === "" ? null : Number(value.commissionPercent),
    products: value.products,
  };
}

export default function CreateAffiliatePartnerPage() {
  const navigate = useNavigate();
  const [formValue, setFormValue] = useState(createEmptyAffiliatePartnerForm());
  const productsQuery = useProductsList({ page: 1, limit: 200 });
  const createMutation = useCreateAffiliatePartnerMutation();

  const handleSubmit = (value: AffiliatePartnerFormValues) => {
    createMutation.mutate(mapFormToBody(value), {
      onSuccess: () => navigate("/affiliates"),
    });
  };

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Add Affiliate Partner</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/affiliates")}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <FiX />
          Back to Affiliates
        </button>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <AffiliatePartnerForm
          title="Product Access"
          submitLabel="Create Affiliate Partner"
          value={formValue}
          products={productsQuery.data?.items ?? []}
          loadingProducts={productsQuery.isLoading}
          submitting={createMutation.isPending}
          errorMessage={extractApiErrorMessage(createMutation.error)}
          onChange={setFormValue}
          onSubmit={handleSubmit}
          onCancel={() => navigate("/affiliates")}
        />
      </section>
    </AdminLayout>
  );
}
